// Eskom PARCC — list pagination (hook + footer), shared by both themes.
//
// PARITY, WITH THEME-APPROPRIATE TUNING. The *control* exists in Legacy and
// Lumen alike — the Findings register renders ~1,600 rows today with no pager at
// all, so this is a straight win in both. What differs is the page SIZE: Lumen
// follows the handoff's calm 7–8 rows (scaled by density), while Legacy keeps a
// dense 50 so an existing user's screen still reads the way it always has. Same
// buttons, same semantics, same accessible names.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useVariant } from '../../lib/uiPrefs';

/** Rows per page for the Lumen density steps (handoff: 7–8 per page). */
const LUMEN_PAGE_SIZE: Record<string, number> = {
  comfort: 7,
  balanced: 8,
  compact: 12,
};

/** Legacy's page: large enough that the dense register still reads as one list. */
const LEGACY_PAGE_SIZE = 50;

function currentDensity(): string {
  if (typeof document === 'undefined') return 'balanced';
  return document.documentElement.dataset.density ?? 'balanced';
}

export interface Pagination<T> {
  page: number;
  pages: number;
  pageSize: number;
  /** The rows for the current page. */
  slice: T[];
  /** 1-based index of the first row shown (0 when empty). */
  from: number;
  /** 1-based index of the last row shown (0 when empty). */
  to: number;
  total: number;
  setPage: (page: number) => void;
  prev: () => void;
  next: () => void;
  canPrev: boolean;
  canNext: boolean;
  /** True when the list overflows one page — the footer only renders then. */
  overflows: boolean;
}

/**
 * Slice `rows` into pages. The page resets to 1 whenever the row COUNT changes
 * (a filter narrowed the list), so a user never lands on an empty page 9.
 *
 * @param rows the full, already-filtered and already-sorted list
 * @param pageSize override the theme default (rarely needed)
 */
export function usePagination<T>(rows: readonly T[], pageSize?: number): Pagination<T> {
  const variant = useVariant();
  const size =
    pageSize ?? (variant === 'lumen' ? (LUMEN_PAGE_SIZE[currentDensity()] ?? 8) : LEGACY_PAGE_SIZE);

  const [page, setPageState] = useState(1);
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / size));

  // Clamp rather than reset blindly: a row disappearing off the end of page 5
  // should leave the reader on page 5, not throw them back to the top.
  useEffect(() => {
    setPageState((p) => Math.min(Math.max(1, p), Math.max(1, Math.ceil(total / size))));
  }, [total, size]);

  const safePage = Math.min(Math.max(1, page), pages);
  const slice = useMemo(
    () => rows.slice((safePage - 1) * size, safePage * size),
    [rows, safePage, size],
  );

  const setPage = useCallback(
    (next: number) =>
      setPageState(Math.min(Math.max(1, next), Math.max(1, Math.ceil(total / size)))),
    [total, size],
  );

  return {
    page: safePage,
    pages,
    pageSize: size,
    slice: slice as T[],
    from: total === 0 ? 0 : (safePage - 1) * size + 1,
    to: Math.min(safePage * size, total),
    total,
    setPage,
    prev: () => setPage(safePage - 1),
    next: () => setPage(safePage + 1),
    canPrev: safePage > 1,
    canNext: safePage < pages,
    overflows: total > size,
  };
}

/**
 * The pager footer. Renders nothing when the list fits one page, so a short
 * register is never cluttered by dead controls.
 *
 * `noun` names the rows ("transaction", "finding") for the count line.
 */
export function Pager<T>({
  pagination,
  noun = 'record',
}: {
  pagination: Pagination<T>;
  noun?: string;
}): React.JSX.Element | null {
  const { from, to, total, page, pages, prev, next, canPrev, canNext, overflows } = pagination;
  if (!overflows) return null;
  return (
    <div
      className="lm-pager"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        paddingTop: 12,
        marginTop: 8,
        borderTop: '1px solid var(--line)',
      }}
    >
      <div className="small" aria-live="polite">
        Showing {from.toLocaleString('en-ZA')}–{to.toLocaleString('en-ZA')} of{' '}
        {total.toLocaleString('en-ZA')} {noun}
        {total === 1 ? '' : 's'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" className="btn sm" onClick={prev} disabled={!canPrev}>
          Prev
        </button>
        <span className="small tnum" style={{ minWidth: 54, textAlign: 'center' }}>
          {page} / {pages}
        </span>
        <button type="button" className="btn sm" onClick={next} disabled={!canNext}>
          Next
        </button>
      </div>
    </div>
  );
}
